import React, { useEffect, useState } from 'react';
import * as S from './AccessOverlay.style';
import { useStore } from '../../store/useStore';
import { requestMagicLink, createCheckout } from '../../services/api';
import { MarketMicrostructureBackdrop } from './MarketMicrostructureBackdrop.component';
import { trackEvent } from '../../services/analytics';

type OverlayMode = 'email' | 'subscribe';

interface Props {
  initialMode?: OverlayMode;
  onClose?: () => void;
}

export const AccessOverlay: React.FC<Props> = ({ initialMode, onClose }) => {
  const { closeReason, appState, tier } = useStore();
  const [mode, setMode] = useState<OverlayMode>(initialMode || 'email');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    type: 'success' | 'error';
  } | null>(null);

  // Determine what to show based on close reason or explicit mode
  const showEmailForm =
    mode === 'email' || closeReason === 'preview_expired' || closeReason === 'unauthorized';

  const showSubscribe =
    mode === 'subscribe' || closeReason === 'trial_expired' || closeReason === 'paywall';

  useEffect(() => {
    if (showEmailForm) {
      trackEvent('overlay_email_shown');
    }

    if (showSubscribe) {
      trackEvent('overlay_subscribe_shown');
    }

    if (closeReason === 'preview_expired') {
      trackEvent('overlay_reason_preview_expired');
    } else if (closeReason === 'trial_expired') {
      trackEvent('overlay_reason_trial_expired');
    } else if (closeReason === 'paywall') {
      trackEvent('overlay_reason_paywall');
    } else if (closeReason === 'unauthorized') {
      trackEvent('overlay_reason_unauthorized');
    } else {
      trackEvent('overlay_reason_manual');
    }
  }, [closeReason, showEmailForm, showSubscribe]);

  // If not blocked and no explicit mode, don't show overlay
  if (appState !== 'blocked' && !initialMode) {
    return null;
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || loading) return;

    setLoading(true);
    setMessage(null);
    trackEvent('magic_link_request_overlay_attempt');

    try {
      await requestMagicLink(email);
      trackEvent('magic_link_request_overlay_success');
      setMessage({
        text: 'Check your email for the magic link',
        type: 'success',
      });
    } catch (err) {
      trackEvent('magic_link_request_overlay_error');
      setMessage({
        text: err instanceof Error ? err.message : 'Failed to send email',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    setMessage(null);
    trackEvent('checkout_overlay_attempt');

    try {
      const { url } = await createCheckout();
      trackEvent('checkout_overlay_redirect');
      window.location.href = url;
    } catch (err) {
      trackEvent('checkout_overlay_error');
      setMessage({
        text: err instanceof Error ? err.message : 'Failed to create checkout session',
        type: 'error',
      });
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (closeReason === 'preview_expired') return 'Preview ended';
    if (closeReason === 'trial_expired') return 'Trial ended';
    if (closeReason === 'paywall') return 'Upgrade to pro';
    if (showEmailForm) return 'Read structure in real time';
    return 'Upgrade to pro';
  };

  const getDescription = () => {
    if (closeReason === 'preview_expired') {
      return 'Enter your email to unlock 24 hours of structural awareness: full-resolution microstructure, regime context, and sonic encoding.';
    }
    if (closeReason === 'trial_expired' || closeReason === 'paywall') {
      return 'Your trial has ended. Upgrade to pro to keep reading market structure in real time.';
    }
    if (showEmailForm) {
      return '24 hours of structural awareness. Per-tick microstructure, AI regime context, sonic encoding. No credit card required.';
    }
    return 'Structural awareness, continuously. Full live access \u00b7 $19/month';
  };

  return (
    <S.Overlay onClick={onClose}>
      <S.BackgroundLayer>
        <MarketMicrostructureBackdrop />
      </S.BackgroundLayer>
      <S.BackgroundVeil />
      <S.Card onClick={(e) => e.stopPropagation()}>
        {onClose && <S.CloseButton onClick={onClose}>[ x ]</S.CloseButton>}
        <S.Title>{getTitle()}</S.Title>
        <S.Description>{getDescription()}</S.Description>

        {showEmailForm && !showSubscribe && (
          <S.Form onSubmit={handleEmailSubmit}>
            <S.Input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              autoFocus
            />
            <S.Button type="submit" disabled={loading || !email}>
              {loading ? '[ sending... ]' : '[ send access link ]'}
            </S.Button>
            {message && <S.Message $type={message.type}>{message.text}</S.Message>}
          </S.Form>
        )}

        {showSubscribe && (
          <>
            <S.Button onClick={handleSubscribe} disabled={loading}>
              {loading ? '[ redirecting... ]' : '[ upgrade to pro ]'}
            </S.Button>
            {message && <S.Message $type={message.type}>{message.text}</S.Message>}

            {tier === 'blocked' && closeReason !== 'trial_expired' && (
              <>
                <S.Divider>
                  <span>or</span>
                </S.Divider>
                <S.SecondaryButton
                  type="button"
                  onClick={() => {
                    trackEvent('overlay_switch_to_email');
                    setMode('email');
                  }}
                >
                  [ start free trial ]
                </S.SecondaryButton>
              </>
            )}
          </>
        )}
      </S.Card>
    </S.Overlay>
  );
};
